import { useOnClickOutside } from "@smartsuite/react-ui/lib/hooks/onClickOutside/useOnClickOutside";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useMenuNavigation } from "./useMenuNavigation";
import { canStartSuggester } from "./suggest";
export function useSuggester({ suggesterCallbacksRegistry, commandsLength, menuRef, commandsRowLength = 1, onEnter, onParamsChange, }) {
    const [state, setState] = useState({
        visible: false,
        params: null,
    });
    const { activeIndex, set, up, down, left, right } = useMenuNavigation({
        length: commandsLength,
        rowLength: commandsRowLength,
    });
    const shouldRender = state.visible && !!state.params && commandsLength > 0;
    useOnClickOutside(useMemo(() => [menuRef], [menuRef]), useCallback(() => {
        if (state.visible) {
            setState((curr) => ({ ...curr, visible: false }));
            // after clicking outside, avoid suggester to be opened again until starter character is inserted again
            state.params.addIgnored({
                from: state.params.range.from,
                char: state.params.suggester.char,
                name: state.params.suggester.name,
            });
        }
    }, [state]), null, ["[id-exclude-click-outside]"]);
    useEffect(() => {
        suggesterCallbacksRegistry.register({
            onChange: (suggesterParams) => {
                setState((curr) => ({ ...curr, params: suggesterParams }));
                if (!state.params &&
                    canStartSuggester(suggesterParams.view, suggesterParams.range.from)) {
                    setState((curr) => ({ ...curr, visible: true }));
                }
            },
            onExit: () => {
                setState({ visible: false, params: null });
            },
            Escape: ({ event, addIgnored, range, suggester }) => {
                if (!shouldRender) {
                    return false;
                }
                event.stopPropagation(); // avoid closing the modal containing the editor in many situations
                setState({ visible: false, params: null });
                // after Escape is pressed, avoid suggester to be opened again until starter character is inserted again
                addIgnored({
                    from: range.from,
                    char: suggester.char,
                    name: suggester.name,
                });
                return true;
            },
            Enter: ({ event }) => {
                if (!shouldRender) {
                    return false;
                }
                event.stopPropagation();
                onEnter(activeIndex);
                return true;
            },
            ArrowDown: () => {
                if (!shouldRender) {
                    return false;
                }
                down();
                return true;
            },
            ArrowUp: () => {
                if (!shouldRender) {
                    return false;
                }
                up();
                return true;
            },
            ArrowLeft: () => {
                if (!shouldRender || commandsRowLength === 1) {
                    return false;
                }
                left();
                return true;
            },
            ArrowRight: () => {
                if (!shouldRender || commandsRowLength === 1) {
                    return false;
                }
                right();
                return true;
            },
        });
    }, [
        suggesterCallbacksRegistry,
        commandsLength,
        commandsRowLength,
        activeIndex,
        state,
        shouldRender,
        up,
        down,
        left,
        right,
        onEnter,
        onParamsChange,
    ]);
    useEffect(() => {
        onParamsChange(state.params);
    }, [state.params, onParamsChange]);
    useEffect(() => {
        if (!shouldRender) {
            set(0);
        }
    }, [set, shouldRender]);
    return useMemo(() => ({
        visible: shouldRender,
        activeIndex,
        setActiveIndex: set,
    }), [shouldRender, activeIndex, set]);
}
